import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection, CollectionReference, Query } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import { combineLatest, from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TemplateInterface } from '../interfaces/template.interface';
import { WidgetAppTemplate } from '../interfaces/widget-app-template';
import { AuthService } from './auth.service';

const TEMPLATES_COLLECTION = 'templates';
const APPS_TEMPLATES_COLLECTION = 'widget-apps-templates';

@Injectable()
export class TemplateService {
    templatesCollection: AngularFirestoreCollection<TemplateInterface>;
    widgetAppsTemplatesCollection: AngularFirestoreCollection<WidgetAppTemplate>;

    constructor(private readonly firestore: AngularFirestore, private readonly authService: AuthService) {
        this.templatesCollection = this.firestore.collection<TemplateInterface>(TEMPLATES_COLLECTION);
        this.widgetAppsTemplatesCollection = this.firestore.collection<WidgetAppTemplate>(APPS_TEMPLATES_COLLECTION);
    }

    getAll(): Observable<TemplateInterface[]> {
        return this.templatesCollection.valueChanges();
    }

    getAllBasic(): Observable<TemplateInterface[]> {
        return this.firestore
            .collection<TemplateInterface>(
                TEMPLATES_COLLECTION,
                (ref: CollectionReference): Query => ref.where('basic', '==', true)
            )
            .valueChanges();
    }

    getAllApps(): Observable<WidgetAppTemplate[]> {
        return this.widgetAppsTemplatesCollection.valueChanges();
    }

    getAllAllowed(sitesIds: string[]): Observable<TemplateInterface[]> {
        const queries = new Array<Observable<TemplateInterface[]>>();
        for (const siteId of sitesIds) {
            const templates$ = this.getAllNoBasicBySite(siteId);
            queries.push(templates$);
        }

        return combineLatest(queries).pipe(
            map((allTemplates: TemplateInterface[][]): TemplateInterface[] => {
                const templates = [];
                for (const t of allTemplates) {
                    templates.push(...t);
                }
                return templates.filter(
                    (template: TemplateInterface, index: number): boolean =>
                        templates.findIndex((t: TemplateInterface): boolean => template.id === t.id) === index
                );
            })
        );
    }

    getAllBySite(siteId: string): Observable<TemplateInterface[]> {
        return this.firestore
            .collection<TemplateInterface>(
                TEMPLATES_COLLECTION,
                (ref: CollectionReference): Query => ref.where('site', '==', siteId)
            )
            .valueChanges();
    }

    getAllNoBasicBySite(siteId: string): Observable<TemplateInterface[]> {
        return this.firestore
            .collection<TemplateInterface>(
                TEMPLATES_COLLECTION,
                (ref: CollectionReference): Query => ref.where('basic', '==', false).where('site', '==', siteId)
            )
            .valueChanges();
    }

    get(templateId: string): Observable<TemplateInterface> {
        return this.templatesCollection.doc<TemplateInterface>(templateId).valueChanges();
    }

    add(template: TemplateInterface): Observable<void> {
        const templateId = this.firestore.createId();
        template.id = templateId;
        template.createdAt = firebase.firestore.Timestamp.now();
        template.updatedAt = firebase.firestore.Timestamp.now();
        template.updatedBy = this.authService.getEmail();
        return from(this.templatesCollection.doc<TemplateInterface>(templateId).set(Object.assign({}, template)));
    }

    delete(templateId: string): Observable<void> {
        return this.update(templateId, { deletedBy: this.authService.getEmail() }).pipe(
            switchMap(
                (): Observable<void> => from(this.templatesCollection.doc<TemplateInterface>(templateId).delete())
            )
        );
    }

    update(templateId: string, dataToUpdate: Partial<TemplateInterface>): Observable<void> {
        dataToUpdate.updatedAt = firebase.firestore.Timestamp.now();
        dataToUpdate.updatedBy = this.authService.getEmail();
        return from(
            this.templatesCollection.doc<TemplateInterface>(templateId).update(Object.assign({}, dataToUpdate))
        );
    }
}
